import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  IDistributionModelingPreset,
  IDistributionModelingRound,
  getFinanceDistributionModelingPresetList,
  getFinanceDistributionModelingRoundList,
} from "api";
import { Nullable } from "config";
import { RootState, store } from "store";

import {
  FinanceDistributionModelingProjectModalMode,
  FinanceDistributionModelingRoundModalMode,
} from "./enums";
import {
  IFinanceDistributionModelingProjectModalData,
  IFinanceDistributionModelingRoundModalData,
} from "./types";

const initialState = {
  loading: false,
  productFundId: null as Nullable<number>,
  roundList: null as Nullable<IDistributionModelingRound[]>,
  roundModalData: {
    mode: FinanceDistributionModelingRoundModalMode.VIEW,
    open: false,
    loading: false,
  } as IFinanceDistributionModelingRoundModalData,
  projectList: null as Nullable<IDistributionModelingPreset[]>,
  projectModalData: {
    mode: FinanceDistributionModelingProjectModalMode.VIEW,
    open: false,
    loading: false,
  } as IFinanceDistributionModelingProjectModalData,
};

export const fetchFinanceDistributionModelingRoundById = createAsyncThunk(
  "finance-distribution-modeling/fetchRoundById",
  async (roundId: number) => {
    const roundList = (store.getState() as RootState)
      .FinanceDistributionModelingReducer.roundList;
    const round = roundList?.find((el) => el.id === roundId);

    return round;
  }
);

export const fetchFinanceDistributionModelingProjectById = createAsyncThunk(
  "finance-distribution-modeling/fetchProjectById",
  async (projectId: number) => {
    const projectList = (store.getState() as RootState)
      .FinanceDistributionModelingReducer.projectList;
    const project = projectList?.find((el) => el.id === projectId);

    return project;
  }
);

export const fetchFinanceDistributionModelingData = createAsyncThunk(
  "finance-distribution-modeling/fetchFinanceDistributionModelingData",
  async (productFundId: number) => {
    const [roundList, projectList] = await Promise.all([
      getFinanceDistributionModelingRoundList(productFundId),
      getFinanceDistributionModelingPresetList(productFundId),
    ]);

    return { roundList, projectList };
  }
);

const financeDistributionModelingSlice = createSlice({
  name: "financeDistributionModeling",
  initialState,
  reducers: {
    setFinanceDistributionModelingProductFundId: (
      state,
      action: PayloadAction<number>
    ) => {
      state.productFundId = action.payload;
    },
    setFinanceDistributionModelingRoundModalData: (
      state,
      action: PayloadAction<IFinanceDistributionModelingRoundModalData>
    ) => {
      state.roundModalData = action.payload;
    },
    closeFinanceDistributionModelingRoundModal: (state) => {
      state.roundModalData = initialState.roundModalData;
    },
    setFinanceDistributionModelingProjectModalData: (
      state,
      action: PayloadAction<IFinanceDistributionModelingProjectModalData>
    ) => {
      state.projectModalData = action.payload;
    },
    closeFinanceDistributionModelingProjectModal: (state) => {
      state.projectModalData = initialState.projectModalData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchFinanceDistributionModelingData.fulfilled,
      (state, action) => {
        if (!action.payload) return;
        return {
          ...state,
          loading: false,
          roundList: action.payload.roundList,
          projectList: action.payload.projectList,
        };
      }
    );
    builder.addCase(fetchFinanceDistributionModelingData.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(fetchFinanceDistributionModelingData.rejected, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(
      fetchFinanceDistributionModelingProjectById.fulfilled,
      (state) => {
        return {
          ...state,
          loading: false,
        };
      }
    );
    builder.addCase(
      fetchFinanceDistributionModelingProjectById.pending,
      (state) => {
        return {
          ...state,
          loading: true,
        };
      }
    );
    builder.addCase(
      fetchFinanceDistributionModelingProjectById.rejected,
      (state) => {
        return {
          ...state,
          loading: false,
        };
      }
    );
    builder.addCase(
      fetchFinanceDistributionModelingRoundById.fulfilled,
      (state) => {
        return {
          ...state,
          loading: false,
        };
      }
    );
    builder.addCase(
      fetchFinanceDistributionModelingRoundById.pending,
      (state) => {
        return {
          ...state,
          loading: true,
        };
      }
    );
    builder.addCase(
      fetchFinanceDistributionModelingRoundById.rejected,
      (state) => {
        return {
          ...state,
          loading: false,
        };
      }
    );
  },
});

export const {
  setFinanceDistributionModelingProductFundId,
  setFinanceDistributionModelingRoundModalData,
  closeFinanceDistributionModelingRoundModal,
  setFinanceDistributionModelingProjectModalData,
  closeFinanceDistributionModelingProjectModal,
} = financeDistributionModelingSlice.actions;

export default financeDistributionModelingSlice.reducer;
